'use client';

import { useCurrentTime } from '@shared/hooks';
import { type ReactNode, useState } from 'react';
import { useLotteryNextDraw } from '../../../lotteries';
import type { LotteryName } from '../../../lottery-purchases/types';
import { getResultsDrawStatus, isLiveDrawInProgress, isPastLiveDrawWindow } from '../../utils';
import { LotteryDrawStatus } from '../LotteryDrawStatus/LotteryDrawStatus';

type ResultsStatusOrNumbersProps = {
  lotteryName: LotteryName;
  lotteryResult: ReactNode;
  /** Drawing instant of the date-picker selection. Keeps "Live draw in progress" up while that draw is still animating. */
  drawingDate?: string;
  /**
   * The selection already has its numbers. They stay hidden until that draw has started, and while
   * it is still animating. A later draw's countdown is not this drawing's status.
   */
  hasResult?: boolean;
};

type LiveDrawStatusGateProps = {
  lotteryName: LotteryName;
  lotteryResult: ReactNode;
  closingDate: number;
  drawingDate: number;
  selectedDrawingDate?: number;
  hasResult: boolean;
};

const UPDATE_PERIOD = 200;
const ONE_SECOND = 1000;
const ONE_MINUTE = 60 * ONE_SECOND;
const ONE_HOUR = 60 * ONE_MINUTE;
const HOUR_PLUS_CLOCK = { updatePeriod: ONE_SECOND, resolution: ONE_MINUTE } as const;
const SUB_HOUR_CLOCK = { updatePeriod: UPDATE_PERIOD, resolution: ONE_SECOND } as const;

/**
 * Next-draw advances to the following drawing as soon as sales close. A page loaded in that gap
 * still has today's unpublished draw selected, so the countdown follows that drawing. Its close is
 * the same interval before its draw as the next draw reports.
 */
function getStatusWindow(closingDate: number, drawingDate: number, selectedDrawingDate: number | undefined, followSelection: boolean) {
  if (!followSelection || selectedDrawingDate == null) {
    return { closingDate, drawingDate };
  }

  return {
    closingDate: selectedDrawingDate - (drawingDate - closingDate),
    drawingDate: selectedDrawingDate,
  };
}

/**
 * ResultsStatusOrNumbers switches between winning numbers and draw status for the live-draw path.
 *
 * Next-draw data is loaded here. Time-based status is evaluated in LiveDrawStatusGate so the clock
 * does not re-render the query. Once next-draw has moved on, a selected draw that has not finished
 * keeps its own close and draw, so a refresh between close and drawing still shows the countdown.
 * The gate uses a minute-resolution clock until one hour remains until close, or while a selected
 * draw is animating, then a 200ms period with one-second resolution. The fine clock stays on until
 * the minute-resolution time is also outside that animation, so dropping resolution cannot floor
 * `now` back into the window.
 *
 * @param {ResultsStatusOrNumbersProps} props - The props for the ResultsStatusOrNumbers component.
 * @param {LotteryName} props.lotteryName - The name of the lottery.
 * @param {ReactNode} props.lotteryResult - The winning-numbers view to show when status is `numbers`.
 * @returns {JSX.Element} The rendered numbers or draw-status banner.
 */
function ResultsStatusOrNumbers({ lotteryName, lotteryResult, drawingDate, hasResult = false }: ResultsStatusOrNumbersProps) {
  const { data } = useLotteryNextDraw(lotteryName);
  const nextDraw = data?.find(draw => draw.lotteryName === lotteryName);
  const closingDate = nextDraw?.closingDate.regular;
  const nextDrawingDate = nextDraw?.drawingDate;
  const selectedDrawingDate = drawingDate === undefined ? undefined : Date.parse(drawingDate);

  if ((closingDate == null || nextDrawingDate == null) && !Number.isFinite(selectedDrawingDate)) {
    return lotteryResult;
  }

  return (
    <LiveDrawStatusGate
      lotteryName={lotteryName}
      lotteryResult={lotteryResult}
      closingDate={closingDate ?? selectedDrawingDate!}
      drawingDate={nextDrawingDate ?? selectedDrawingDate!}
      selectedDrawingDate={Number.isFinite(selectedDrawingDate) ? selectedDrawingDate : undefined}
      hasResult={hasResult}
    />
  );
}

function LiveDrawStatusGate({ lotteryName, lotteryResult, closingDate, drawingDate, selectedDrawingDate, hasResult }: LiveDrawStatusGateProps) {
  const [useFineClock, setUseFineClock] = useState(false);
  const now = useCurrentTime(useFineClock ? SUB_HOUR_CLOCK : HOUR_PLUS_CLOCK);
  const minuteStart = Math.floor(now / ONE_MINUTE) * ONE_MINUTE;
  const selectedDrawIsAhead = selectedDrawingDate != null && now < selectedDrawingDate;
  const countdownFollowsSelection = !hasResult || selectedDrawIsAhead;
  const followUnpublishedSelection =
    countdownFollowsSelection && selectedDrawingDate != null && selectedDrawingDate < drawingDate && !isPastLiveDrawWindow(lotteryName, selectedDrawingDate, now);
  const statusWindow = getStatusWindow(closingDate, drawingDate, selectedDrawingDate, followUnpublishedSelection);
  const remainingMs = statusWindow.closingDate - now;
  // Numbers that have already been drawn only wait on that draw finishing, not on the next close.
  // A draw that has not started still follows its own close. Hold the fine clock while the minute
  // floor is still inside the animation. Dropping to minute resolution there floors `now` backwards
  // into the window and the two clocks flip forever.
  const nextUseFineClock =
    (countdownFollowsSelection && remainingMs <= ONE_HOUR) ||
    isLiveDrawInProgress(lotteryName, selectedDrawingDate, now) ||
    isLiveDrawInProgress(lotteryName, selectedDrawingDate, minuteStart);

  if (nextUseFineClock !== useFineClock) {
    setUseFineClock(nextUseFineClock);
  }

  if (isLiveDrawInProgress(lotteryName, selectedDrawingDate, now)) {
    return <LotteryDrawStatus status="live_draw_in_progress" closingDate={selectedDrawingDate!} drawingDate={selectedDrawingDate!} className="light" />;
  }

  if (hasResult && !selectedDrawIsAhead) {
    return lotteryResult;
  }

  // The selected drawing has finished animating, but its numbers are not in the results payload yet.
  if (isPastLiveDrawWindow(lotteryName, selectedDrawingDate, now)) {
    return <LotteryDrawStatus status="awaiting_results" closingDate={selectedDrawingDate!} drawingDate={selectedDrawingDate!} className="light" />;
  }

  const status = getResultsDrawStatus({ now, closingDate: statusWindow.closingDate, drawingDate: statusWindow.drawingDate, lotteryName });

  if (status === 'numbers') {
    return lotteryResult;
  }

  return <LotteryDrawStatus status={status} closingDate={statusWindow.closingDate} drawingDate={statusWindow.drawingDate} className="light" />;
}

export { ResultsStatusOrNumbers };
